import type { ReactNode } from "react";

import { OPSBELT_APP_URL } from "../lib/env";
import { useMe } from "../lib/hooks";

/** Centered, card-framed state used across data-driven pages. */
function StateShell({
  icon,
  iconClass = "bg-surface-100 text-ink-subtle",
  title,
  description,
  action,
}: {
  icon: ReactNode;
  iconClass?: string;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="card flex flex-col items-center justify-center px-6 py-16 text-center">
      <div className={`mb-4 flex h-11 w-11 items-center justify-center rounded-xl ${iconClass}`}>
        {icon}
      </div>
      <h3 className="font-display text-base font-semibold text-ink">{title}</h3>
      {description && (
        <p className="mt-1.5 max-w-sm text-sm leading-relaxed text-ink-muted">
          {description}
        </p>
      )}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <StateShell
      icon={
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 12h-6l-2 3h-4l-2-3H2" />
          <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
        </svg>
      }
      title={title}
      description={description}
      action={action}
    />
  );
}

export function Loading({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2.5 px-1 py-16 text-sm text-ink-muted">
      <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-surface-300 border-t-accent" />
      {label}
    </div>
  );
}

export function ErrorState({
  error,
  onRetry,
}: {
  error: unknown;
  onRetry?: () => void;
}) {
  const { data: me } = useMe();
  const isAdmin = me?.org.role === "owner" || me?.org.role === "admin";
  const message =
    error instanceof Error ? error.message : "Something went wrong.";
  // A 402 (no seat) reads as "get access", not a hard error.
  const isSubscription = /not subscribed|subscription|seat|organization/i.test(
    message,
  );

  return (
    <StateShell
      iconClass={
        isSubscription ? "bg-accent/15 text-accent" : "bg-warn/15 text-warn"
      }
      icon={
        isSubscription ? (
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2 2 7l10 5 10-5-10-5z" /><path d="M2 17l10 5 10-5M2 12l10 5 10-5" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <path d="M12 9v4M12 17h.01" />
          </svg>
        )
      }
      title={
        isSubscription
          ? isAdmin
            ? "Set up BlobShip for your team"
            : "You don't have BlobShip access yet"
          : "Couldn't load this"
      }
      description={
        isSubscription
          ? isAdmin
            ? "Manage your BlobShip plan and seats in the OpsBelt admin — then assign yourself a seat."
            : "Your workspace hasn't given you a BlobShip seat. Request one and an admin can approve it."
          : message
      }
      action={
        isSubscription ? (
          <a
            className="btn-primary"
            target="_blank"
            rel="noopener noreferrer"
            href={
              isAdmin
                ? `${OPSBELT_APP_URL}/billing`
                : `${OPSBELT_APP_URL}/apps?request=blobship`
            }
          >
            {isAdmin ? "Manage in OpsBelt ↗" : "Request access ↗"}
          </a>
        ) : onRetry ? (
          <button className="btn-primary" onClick={onRetry} type="button">
            Try again
          </button>
        ) : undefined
      }
    />
  );
}
